'use client'
import { Link2Icon } from '@radix-ui/react-icons'
import { useTeamReplicache } from '~/hooks-data/use-team-replicache'
import { Button, type ButtonProps } from '~/sotano-kit/primitives/button'
import { getId } from '@basehub/utils/id/index'
import { toast } from 'sonner'
import * as React from 'react'
import {
  getInviteLink,
  isInviteExpired,
  isInviteGeneric,
} from '@basehub/data/replicache/mutators/team-space/invite-util'
import ph from 'posthog-js'
import * as va from '@vercel/analytics'
import { isUserTeam } from '@basehub/data/replicache/mutators/_public-types'
import { Role, Team, roleSchema } from '@basehub/data/replicache/index'
import { InviteForm } from '~/app/(dash)/_components/invite-form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/sotano-kit/primitives/select'
import { SelectProps } from '@radix-ui/react-select'
import { clsx } from 'clsx'
import { useTeamMembers } from '~/hooks-data/use-team-members'
import { useSelectedTeamAndRepo } from '~/hooks-data/use-selected-team-and-repo'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/sotano-kit/primitives/dialog'
import { Input } from '~/sotano-kit/primitives/input'
import { CopyTooltip } from '~/sotano-kit/primitives/copy-tooltip'
import { rIC } from '~/lib/request-idle-callback'
import { useGenericInvite } from '~/hooks-data/use-generic-invite'
import { RepoSelector } from '~/sotano-kit/primitives/repo-selector'
import { useReplicacheQueryV2 } from '~/hooks-data/use-replicache-query'
import { useUserStore } from '~/app/_store/user-store'
import { invariant } from '@epic-web/invariant'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from '@radix-ui/react-accordion'
import { Switch, SwitchThumb } from '~/sotano-kit/primitives/switch'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'
import { teamSpaceMutators } from '@basehub/data/replicache/mutators/team-space/mutators'

export const TeamInviteMemberForm = () => {
  const { data } = useTeamReplicache()
  const currentUserId = useUserStore((s) => s.optimisticUser?.id)
  const { replicache, team } = data || {}
  const [inviteLinkRole, setInviteLinkRole] = React.useState<Role>('member')
  const membersQuery = useTeamMembers()
  const [reposSelected, setReposSelected] = React.useState<string[]>([])
  const [hasAllAccess, setHasAllAccess] = React.useState(true)
  const [emailRole, setEmailRole] = React.useState<Role>('member')
  const repositoriesQuery = useReplicacheQueryV2({
    replicache,
    fetcher: (tx) => {
      invariant(team?.id, "No team ID, shouldn't be enabled.")
      invariant(currentUserId, "No user ID, shouldn't be enabled.")

      return teamSpaceMutators.listRepositories(tx, {
        teamId: team?.id,
        userId: currentUserId,
      })
    },
    key: ['repositories', team?.id, currentUserId],
    enabled: Boolean(team?.id && currentUserId),
  })

  React.useEffect(() => {
    if (emailRole === 'owner') setHasAllAccess(true)
  }, [emailRole])

  return (
    <div>
      <InviteForm
        className="flex items-end"
        inputProps={{
          className: 'pr-[200px]',
          onKeyDown: (e) => {
            if (e.key === 'Enter' && e.metaKey) {
              e.preventDefault()
              e.currentTarget.form?.requestSubmit()
            }
          },
        }}
        onSubmit={async (e, reset) => {
          const members = membersQuery.data
          if (!members) return

          e.preventDefault()
          const formData = new FormData(e.currentTarget)
          const formDataEmails = formData.get('emails')?.toString()

          const memberEmails = members.map((member) => member.email)
          const emails: string[] = []

          ;(formDataEmails ?? '').split(',').forEach((nonTrimmedEmail) => {
            const email = nonTrimmedEmail.trim().toLocaleLowerCase()
            if (memberEmails.includes(email)) return
            if (
              team?.invites.some(
                (invite) =>
                  invite.emailLock === email &&
                  (invite.emailLock || invite.userIdLock) &&
                  !isInviteExpired(invite) &&
                  invite.useCount === 0
              )
            ) {
              return
            }
            emails.push(email)
          })

          if (!emails.length) return toast.error('Emails already invited')
          if (!formDataEmails) return

          await data?.replicache.mutate
            .bulkInviteEmailsToTeam({
              teamId: data.teamId,
              emails,
              invite: {
                token: getId(),
                createdAt: new Date().toISOString(),
                role: emailRole,
                hasAllAccess,
                userIdLock: null,
                useCount: 0,
                ...(hasAllAccess ? {} : { repoIds: reposSelected }),
              },
            })
            .then(() => {
              toast.success('Invited successfully')
              va.track('Teammates invited', {
                source: 'members settings',
                emails: formDataEmails,
                teamId: data?.teamId ?? '',
              })
              ph.capture('Teammates invited', {
                source: 'members settings',
                emails: formDataEmails,
                teamId: data?.teamId,
              })
            })
            .catch((e) => {
              toast.error('Failed to invite')
              ph.captureException('Failed to invite', {
                source: 'members settings',
                emails: formDataEmails,
                teamId: data?.teamId,
              })
              va.track('Failed to invite', {
                source: 'members settings',
                emails: formDataEmails,
                teamId: data?.teamId,
              })
              console.error(e)
            })
            .finally(() => {
              reset()
            })
        }}
      >
        <div className="absolute bottom-0.5 right-0 flex items-center divide-x-1 pr-2">
          <Dialog>
            <SimpleTooltip
              content="Owners must have access to all repositories."
              disabled={emailRole !== 'owner'}
              asChild
            >
              <DialogTrigger asChild>
                <Button
                  disabled={emailRole === 'owner'}
                  className={clsx(
                    'text-sm text-dark-dim hover:text-dark-bright pb-px pr-2 outline-none focus-visible:ring-1 ring-dark-accent rounded',
                    emailRole === 'owner' &&
                      'cursor-not-allowed text-dark-muted hover:!text-dark-muted'
                  )}
                  unstyled
                >
                  {hasAllAccess
                    ? 'All Repositories'
                    : `${reposSelected.length} Repositories`}
                </Button>
              </DialogTrigger>
            </SimpleTooltip>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Manage invite role and access</DialogTitle>
              </DialogHeader>
              <Accordion
                type="single"
                value={hasAllAccess ? 'all-repos' : 'specific-repos'}
                className="flex flex-col transition-all overflow-hidden px-1 py-1"
              >
                <SimpleTooltip
                  contentClassName="!z-[9999]"
                  align="end"
                  content="Owners must have access to all repositories."
                  disabled={emailRole !== 'owner'}
                >
                  <div
                    className={clsx(
                      'grid grid-cols-[1fr_auto] leading-normal',
                      emailRole === 'owner' && 'cursor-not-allowed'
                    )}
                  >
                    <label
                      htmlFor="all-switch"
                      className="text-dark-bright text-sm font-medium"
                    >
                      Access all repositories
                    </label>
                    <Switch
                      id="all-switch"
                      name="all-switch"
                      className="group"
                      disabled={emailRole === 'owner'}
                      // Force check when role is owner
                      checked={hasAllAccess}
                      onCheckedChange={setHasAllAccess}
                    >
                      <SwitchThumb className="group-disabled:opacity-70" />
                    </Switch>
                    <span className="text-dark-faint text-xs col-span-full">
                      Toggle to give this person access to all current and
                      future repos.
                    </span>
                  </div>
                </SimpleTooltip>
                {repositoriesQuery.data &&
                  repositoriesQuery.data.length > 0 && (
                    <AccordionItem value="specific-repos">
                      <AccordionContent className="data-[state=open]:animate-slide-down data-[state=closed]:animate-slide-up rounded-lg bg-dark-base-dark text-dark-dim !border-none mt-4">
                        <RepoSelector
                          repositories={repositoriesQuery.data ?? []}
                          selectedRepoIds={reposSelected}
                          onSelectionChange={setReposSelected}
                        />
                      </AccordionContent>
                    </AccordionItem>
                  )}
              </Accordion>
            </DialogContent>
          </Dialog>
          <InviteRoleSelect
            name="role-invite-email"
            value={emailRole}
            onValueChange={(v) => setEmailRole(roleSchema.parse(v))}
          />
        </div>
      </InviteForm>

      <div className="flex items-center text-xs mt-2">
        <span className="text-dark-faint">
          Anyone with the invite link will become
        </span>
        &nbsp;
        <InviteRoleSelect
          className="pl-0.5"
          value={inviteLinkRole}
          onValueChange={(v) => setInviteLinkRole(roleSchema.parse(v))}
        />
        <InviteLinkDialog
          unstyled
          scope="team"
          role={inviteLinkRole}
          className="flex items-center gap-x-1 hover:text-dark-dim ml-auto"
        />
      </div>
    </div>
  )
}

export const InviteRoleSelect = ({
  className,
  name,
  ...props
}: SelectProps & { className?: string }) => {
  return (
    <Select defaultValue="member" name={name} {...props}>
      <SelectTrigger
        type="button"
        className={className}
        color="transparent"
        narrow
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="owner">Owner</SelectItem>
        <SelectItem value="member">Member</SelectItem>
        <SelectItem value="editor">Editor</SelectItem>
        <SelectItem value="viewer">Viewer</SelectItem>
      </SelectContent>
    </Select>
  )
}

export const InviteLinkButton = ({
  unstyled,
  size,
  children,
  className,
  ...rest
}: Omit<
  ButtonProps,
  | 'intent'
  | 'type'
  | 'icon'
  | 'focusStyled'
  | 'ref'
  | 'iconPaddingLeft'
  | 'pointer'
  | 'role'
> &
  ({ scope: 'team'; role: Role } | { scope: 'repo' })) => {
  const { data } = useTeamReplicache()
  const selectedTeanAndRepo = useSelectedTeamAndRepo().data
  const team = selectedTeanAndRepo?.team
  const repo = selectedTeanAndRepo?.repo
  const [status, setStatus] = React.useState<'idle' | 'loading' | 'success'>(
    'idle'
  )

  const validGenericInvite = React.useMemo(() => {
    if (rest.scope === 'team' && isUserTeam(team)) {
      return team.invites.find(
        (i) => isInviteGeneric(i) && !isInviteExpired(i) && i.role === rest.role
      )
    } else if (rest.scope === 'repo' && repo) {
      return repo?.guests?.invites.find(
        (i) => isInviteGeneric(i) && !isInviteExpired(i)
      )
    }
  }, [repo, rest, team])

  React.useEffect(() => {
    if (status === 'success') {
      const timeout = setTimeout(() => {
        setStatus('idle')
      }, 2000)
      return () => clearTimeout(timeout)
    }
  }, [status])

  const inviteToTeam = async (role: Team['invites'][number]['role']) => {
    try {
      if (!data) throw new Error('No team data')
      const parsedRole = roleSchema.parse(role)
      let token: string | null = null
      if (validGenericInvite) {
        token = validGenericInvite.token
      } else {
        const { invites } = await data.replicache.mutate.inviteToTeam({
          teamId: data.teamId,
          invite: {
            token: getId(),
            createdAt: new Date().toISOString(),
            role: parsedRole,
            hasAllAccess: true,
            userIdLock: null,
            emailLock: null,
            useCount: 0,
          },
        })
        const [invite] = invites
        if (!invite) return
        token = invite.token
      }

      navigator.clipboard.writeText(getInviteLink(token))
      toast.success('Invite link copied to clipboard')
    } catch (e) {
      toast.error('Failed to generate invite link')
      console.error(e)
    }
  }

  const inviteToRepo = async () => {
    try {
      if (!data || !repo) throw new Error('No repo data')
      let token: string | null = null
      if (validGenericInvite) {
        token = validGenericInvite.token
      } else {
        const { invites } = await data.replicache.mutate.inviteToRepo({
          teamId: data.teamId,
          repoId: repo.id,
          invites: [
            {
              token: getId(),
              createdAt: new Date().toISOString(),
              userIdLock: null,
              emailLock: null,
              useCount: 0,
            },
          ],
        })
        const [firstInvite] = Array.isArray(invites) ? invites : [invites]
        if (!firstInvite) return
        token = firstInvite.token
      }

      navigator.clipboard.writeText(getInviteLink(token))
      toast.success('Invite link copied to clipboard')
    } catch (e) {
      toast.error('Failed to generate invite link')
      console.error(e)
    }
  }

  return (
    // @ts-expect-error ambiguous unstyled types
    <Button
      {...rest}
      {...(!unstyled
        ? { intent: 'secondary', size }
        : { unstyled: true, focusStyled: true })}
      className={clsx(className, unstyled && 'cursor-default')}
      icon={<Link2Icon />}
      type="button"
      disabled={status === 'loading' || rest.disabled}
      onClick={async () => {
        if (rest.scope === 'team') inviteToTeam(rest.role)
        else inviteToRepo()
      }}
    >
      {children ?? 'Copy Invite Link'}
    </Button>
  )
}

export const InviteLinkDialog = ({
  unstyled,
  size,
  children,
  className,
  ...rest
}: Omit<
  ButtonProps,
  | 'intent'
  | 'type'
  | 'icon'
  | 'focusStyled'
  | 'ref'
  | 'iconPaddingLeft'
  | 'pointer'
  | 'role'
> &
  ({ scope: 'team'; role: Role } | { scope: 'repo' })) => {
  const { data } = useTeamReplicache()
  const selectedTeanAndRepo = useSelectedTeamAndRepo().data
  const team = selectedTeanAndRepo?.team ?? null
  const repo = selectedTeanAndRepo?.repo ?? null
  const [status, setStatus] = React.useState<'idle' | 'loading' | 'success'>(
    'idle'
  )
  const validGenericInvite = useGenericInvite(
    rest.scope === 'repo' ? { repo: repo } : { team: team, role: rest.role }
  )

  React.useEffect(() => {
    if (status === 'success') {
      const timeout = setTimeout(() => {
        setStatus('idle')
      }, 2000)
      return () => clearTimeout(timeout)
    }
  }, [status])

  const inviteToTeam = React.useCallback(
    async (role: Team['invites'][number]['role']) => {
      try {
        if (!data) throw new Error('No team data')
        const parsedRole = roleSchema.parse(role)
        if (validGenericInvite) return
        await data.replicache.mutate.inviteToTeam({
          teamId: data.teamId,
          invite: {
            token: getId(),
            createdAt: new Date().toISOString(),
            role: parsedRole,
            hasAllAccess: true,
            userIdLock: null,
            emailLock: null,
            useCount: 0,
          },
        })
      } catch (e) {
        console.error(e)
      }
    },
    [data, validGenericInvite]
  )

  const inviteToRepo = React.useCallback(async () => {
    try {
      if (!data || !repo) throw new Error('No repo data')
      if (validGenericInvite) return
      await data.replicache.mutate.inviteToRepo({
        teamId: data.teamId,
        repoId: repo.id,
        invites: [
          {
            token: getId(),
            createdAt: new Date().toISOString(),
            userIdLock: null,
            emailLock: null,
            useCount: 0,
          },
        ],
      })
    } catch (e) {
      console.error(e)
    }
  }, [data, repo, validGenericInvite])

  const roleAndScope =
    rest.scope === 'team'
      ? ({ scope: 'team', role: rest.role } as const)
      : ({ scope: 'repo' } as const)

  React.useEffect(() => {
    if (!validGenericInvite) {
      if (roleAndScope.scope === 'team') inviteToTeam(roleAndScope.role)
      else inviteToRepo()
    }
  }, [
    inviteToRepo,
    inviteToTeam,
    roleAndScope.role,
    roleAndScope.scope,
    validGenericInvite,
  ])

  const inviteLink = getInviteLink(validGenericInvite?.token ?? '')
  const inputRef = React.useRef<HTMLInputElement>(null)

  return (
    <Dialog>
      <DialogTrigger asChild>
        {/* @ts-expect-error - Ambiguous unstyled types */}
        <Button
          {...rest}
          {...(!unstyled
            ? { intent: 'secondary', size }
            : { unstyled: true, focusStyled: true })}
          className={clsx(className, unstyled && 'cursor-default')}
          icon={<Link2Icon />}
          type="button"
          disabled={status === 'loading' || rest.disabled}
        >
          {children ?? 'Invite Link'}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>Invite Link</DialogTitle>
        <DialogDescription className="mb-4">
          Allow users to join your{' '}
          {rest.scope === 'repo' ? 'repository' : 'team'} through this link
        </DialogDescription>
        <div className="flex">
          <Input
            ref={inputRef}
            readOnly
            value={inviteLink}
            className="rounded-r-none -mr-px"
            size="xs"
          />
          <CopyTooltip
            className="aspect-square z-10 border border-dark-border-solid !rounded-r-md !rounded-l-none bg-dark-shade flex items-center justify-center h-7"
            copyContent={inviteLink}
          />
        </div>

        <DialogFooter divider>
          <DialogClose asChild>
            <Button intent="secondary" autoFocus>
              Cancel
            </Button>
          </DialogClose>
          <Button
            onClick={async () => {
              if (
                (rest.scope === 'repo' && !repo) ||
                !team ||
                !validGenericInvite?.token
              ) {
                return
              }

              if (rest.scope === 'team') {
                await data?.replicache.mutate.resetTeamInvitation({
                  oldInviteToken: validGenericInvite.token,
                  newInvite: {
                    role: rest.role,
                    createdAt: new Date().toISOString(),
                    emailLock: null,
                    token: getId(),
                    useCount: 0,
                    userIdLock: null,
                    hasAllAccess: true,
                  },
                  teamId: team.id,
                })
              } else if (repo) {
                await data?.replicache.mutate.resetRepoInvitation({
                  oldInviteToken: validGenericInvite.token,
                  newInviteToken: getId(),
                  repoId: repo.id,
                  isoNow: new Date().toISOString(),
                  teamId: team.id,
                })
              }
              rIC(() => inputRef.current?.select())
            }}
          >
            Reset invite link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
